/**
 * Mock calendar for `VITE_USE_MOCK=1` (no backend): the user's calendar plus Bob's (reviewer rights), a room
 * (Room 1, 8 seats) and equipment (Projector) with free/busy only, sample meetings in the current week, an
 * invitation that also arrives as a mail, rooms and sharing. Mirrors the platform API rules that matter in the UI:
 * private items of others are masked, free/busy calendars show blocks only, read-only without editor rights.
 */
import { ApiError } from '@platform/api-client';
import { addDays, startOfWeek } from '@platform/ui';
import type { WebmailApi } from './index';
import { matchesRelative } from '../calendar/recurrence';
import type { Address, BookingRequest, CalendarEvent, CalendarEventInput, CalendarInfo, CalendarPermissions, MeetingInfo, MyAttendantSettings, Room } from './types';

type CalendarApi = Pick<
  WebmailApi,
  | 'listCalendars'
  | 'addCalendar'
  | 'createOwnCalendar'
  | 'updateCalendar'
  | 'removeCalendar'
  | 'listEvents'
  | 'getEvent'
  | 'createEvent'
  | 'updateEvent'
  | 'deleteEvent'
  | 'respondToEvent'
  | 'freeBusy'
  | 'schedulingSuggestions'
  | 'roomLists'
  | 'findRooms'
  | 'listRooms'
  | 'calendarPermissions'
  | 'setCalendarPermissions'
  | 'myAttendantSettings'
  | 'setMyAttendantSettings'
  | 'bookingRequests'
  | 'decideBookingRequest'
>;
type Attendee = CalendarEvent['attendees'][number];
type Recurrence = NonNullable<CalendarEvent['recurrence']>;
type Access = CalendarInfo['access'];

const delay = <T>(value: T, ms = 120): Promise<T> => new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), ms));

export const BOB = { name: 'Bob Example', address: 'bob@example.com' };
export const ROOM_1 = { name: 'Room 1', address: 'room1@example.com' };
export const PROJECTOR = { name: 'Projector', address: 'projector@example.com' };
const CAROL = { name: 'Carol Example', address: 'carol@example.com' };
/** The invitation mail of the mock inbox (message id) and its calendar item. */
export const INVITE_MESSAGE_ID = 'm-invite';
const INVITE_EVENT_ID = 'ev-invite';

interface Stored {
  id: string;
  calendar: string;
  subject: string;
  start: Date;
  end: Date;
  allDay: boolean;
  location: string;
  bodyHtml: string;
  showAs: CalendarEvent['showAs'];
  sensitivity: CalendarEvent['sensitivity'];
  organizer: Address | null;
  attendees: Attendee[];
  myResponse: CalendarEvent['myResponse'];
  reminderMinutes: number | null;
  recurrence: Recurrence | null;
  /** Occurrence indexes moved out of the series (changed or deleted on their own). */
  skipped?: number[];
  categories?: string[];
  color?: CalendarEvent['color'];
  onlineMeetingUrl?: string | null;
  reminders?: number[];
}

const ROOMS: Room[] = [
  { ...ROOM_1, kind: 'room', capacity: 8, building: 'HQ', floor: '2', location: '' },
  { name: 'Board room', address: 'boardroom@example.com', kind: 'room', capacity: 14, building: 'HQ', floor: '5', location: 'East wing' },
  { ...PROJECTOR, kind: 'equipment', capacity: null, building: '', floor: '', location: '' },
];
const DIRECTORY: Address[] = [BOB, CAROL, ROOM_1, PROJECTOR, { name: 'Board room', address: 'boardroom@example.com' }, { name: 'Team Test', address: 'team@example.com' }];
const ACCESS: Record<string, Access> = { [BOB.address]: 'reviewer', [CAROL.address]: 'details', 'team@example.com': 'owner' };
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
/** Additional own calendar of the mock (an `f.` folder id like the API's). */
export const HOLIDAYS_CALENDAR = 'f.holidays';
/** The API's permission tables (services/platform-api lib/folder-rights.ts). */
const RIGHT_BITS: Record<string, number> = {
  readAny: 0x1, create: 0x2, sendAs: 0x4, editOwned: 0x8, deleteOwned: 0x10, editAny: 0x20, deleteAny: 0x40, createSubfolder: 0x80,
  folderOwner: 0x100, folderContact: 0x200, folderVisible: 0x400, freeBusySimple: 0x800, freeBusyDetailed: 0x1000, storeOwner: 0x2000,
};
const CUSTOM_RIGHTS = ['readAny', 'create', 'editOwned', 'deleteOwned', 'editAny', 'deleteAny', 'createSubfolder', 'folderOwner', 'folderContact', 'folderVisible', 'freeBusySimple', 'freeBusyDetailed'];
export const ROLES = {
  owner: 0x1ffb, publishingEditor: 0x1cfb, editor: 0x1c7b, publishingAuthor: 0x1c9b, author: 0x1c1b, nonEditingAuthor: 0x1c13,
  reviewer: 0x1c01, contributor: 0xc02, limitedDetails: 0x1800, availabilityOnly: 0x800, none: 0,
} as const;
type PresetRole = keyof typeof ROLES;
const roleOf = (rights: number): PresetRole | 'custom' => (Object.entries(ROLES).find(([, value]) => value === rights)?.[0] as PresetRole | undefined) ?? 'custom';

const at = (day: Date, hour: number, minute = 0) => new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute);
const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const person = (a: Address, type: Attendee['type'], response: Attendee['response']): Attendee => ({ ...a, type, response });
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

function seed(me: Address): Stored[] {
  const monday = startOfWeek(new Date(), 1);
  const day = (n: number) => addDays(monday, n);
  const base = { location: '', bodyHtml: '', showAs: 'busy' as const, sensitivity: 'normal' as const, organizer: null, attendees: [], myResponse: 'organizer' as const, reminderMinutes: 15, recurrence: null, allDay: false };
  return [
    {
      ...base,
      id: 'ev-standup',
      calendar: me.address,
      subject: 'Team standup',
      start: at(addDays(monday, -14), 9),
      end: at(addDays(monday, -14), 9, 15),
      organizer: me,
      attendees: [person(BOB, 'required', 'accepted')],
      recurrence: { pattern: 'weekly', interval: 1, daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], startDate: isoDate(addDays(monday, -14)) },
    },
    {
      ...base,
      id: 'ev-review',
      calendar: me.address,
      subject: 'Project review',
      start: at(day(1), 10),
      end: at(day(1), 11, 30),
      location: 'Room 1',
      bodyHtml: '<p>Agenda:</p><ol><li>Status of the migration</li><li>Calendar rollout</li><li>Next steps</li></ol>',
      organizer: me,
      attendees: [person(BOB, 'required', 'accepted'), person(CAROL, 'optional', 'tentative'), person(ROOM_1, 'resource', 'accepted'), person(PROJECTOR, 'resource', 'accepted')],
    },
    { ...base, id: 'ev-focus', calendar: me.address, subject: 'Focus time', start: at(day(0), 13), end: at(day(0), 15), showAs: 'free', color: 'green' },
    { ...base, id: 'ev-lunch', calendar: me.address, subject: 'Lunch with Clara', start: at(day(2), 12), end: at(day(2), 13), location: 'Canteen', categories: ['Internal'] },
    { ...base, id: 'ev-dentist', calendar: me.address, subject: 'Dentist', start: at(day(2), 16), end: at(day(2), 17), sensitivity: 'private', showAs: 'oof' },
    {
      ...base,
      id: 'ev-workshop',
      calendar: me.address,
      subject: 'Design workshop',
      start: at(day(3), 14),
      end: at(day(3), 15, 30),
      location: 'Board room',
      organizer: BOB,
      attendees: [person(BOB, 'required', 'organizer'), person(me, 'required', 'tentative')],
      myResponse: 'tentative',
      showAs: 'tentative',
    },
    { ...base, id: 'ev-budget', calendar: me.address, subject: 'Budget call', start: at(day(3), 14, 30), end: at(day(3), 15), categories: ['Customer'], onlineMeetingUrl: 'https://meet.example.com/budget' },
    { ...base, id: 'ev-fair', calendar: me.address, subject: 'Trade fair', start: at(day(4), 0), end: at(day(5), 0), allDay: true, showAs: 'oof', location: 'Exhibition centre', reminderMinutes: null },
    { ...base, id: 'ev-holiday', calendar: HOLIDAYS_CALENDAR, subject: 'Summer break', start: at(day(1), 0), end: at(day(2), 0), allDay: true, showAs: 'free', reminderMinutes: null },
    {
      ...base,
      id: INVITE_EVENT_ID,
      calendar: me.address,
      subject: 'Budget review',
      start: at(day(4), 11),
      end: at(day(4), 12),
      location: 'Room 1',
      bodyHtml: '<p>Let us go through the Q4 budget.</p>',
      organizer: BOB,
      attendees: [person(BOB, 'required', 'organizer'), person(me, 'required', 'none'), person(ROOM_1, 'resource', 'accepted')],
      myResponse: 'none',
      showAs: 'tentative',
    },
    { ...base, id: 'bob-call', calendar: BOB.address, subject: 'Customer call', start: at(day(1), 10, 30), end: at(day(1), 11, 30), myResponse: 'organizer' },
    { ...base, id: 'bob-planning', calendar: BOB.address, subject: 'Sprint planning', start: at(day(2), 9, 30), end: at(day(2), 11), location: 'Board room' },
    { ...base, id: 'bob-doctor', calendar: BOB.address, subject: 'Doctor', start: at(day(3), 8), end: at(day(3), 9), sensitivity: 'private' },
    { ...base, id: 'bob-1on1', calendar: BOB.address, subject: '1:1 with Alice', start: at(day(0), 15, 30), end: at(day(0), 16) },
    { ...base, id: 'room-1', calendar: ROOM_1.address, subject: 'Project review', start: at(day(1), 10), end: at(day(1), 11, 30) },
    { ...base, id: 'room-2', calendar: ROOM_1.address, subject: 'Interview', start: at(day(3), 13), end: at(day(3), 14) },
    { ...base, id: 'room-3', calendar: ROOM_1.address, subject: 'Training', start: at(day(2), 15), end: at(day(2), 16) },
    { ...base, id: 'proj-1', calendar: PROJECTOR.address, subject: 'Project review', start: at(day(1), 10), end: at(day(1), 11, 30) },
    { ...base, id: 'proj-2', calendar: PROJECTOR.address, subject: 'All hands', start: at(day(4), 9), end: at(day(4), 10) },
    { ...base, id: 'carol-1', calendar: CAROL.address, subject: 'Supplier visit', start: at(day(1), 13), end: at(day(1), 15), location: 'Reception' },
  ];
}

/** Occurrences of an item in [from, to). */
function expand(item: Stored, from: Date, to: Date): { start: Date; end: Date; index: number }[] {
  const length = item.end.getTime() - item.start.getTime();
  const r = item.recurrence;
  if (!r) return item.start < to && item.end > from ? [{ start: item.start, end: item.end, index: -1 }] : [];
  const out: { start: Date; end: Date; index: number }[] = [];
  const until = r.endDate ? addDays(new Date(`${r.endDate}T00:00:00`), 1) : undefined;
  let count = 0;
  for (let i = 0, d = item.start; i < 800 && d < to; i++, d = addDays(item.start, i)) {
    const weekIndex = Math.floor(i / 7);
    const matches =
      r.pattern === 'daily' ? i % r.interval === 0 : r.pattern === 'weekly' ? weekIndex % r.interval === 0 && (r.daysOfWeek ?? []).includes(WEEKDAYS[d.getDay()] ?? 'monday') : r.weekIndex ? matchesRelative(r, d) : r.pattern === 'monthly' ? d.getDate() === (r.dayOfMonth ?? item.start.getDate()) : d.getDate() === item.start.getDate() && d.getMonth() === item.start.getMonth();
    if (!matches) continue;
    if ((until && d >= until) || (r.count && count >= r.count)) break;
    count++;
    if (d.getTime() + length > from.getTime() && !item.skipped?.includes(i)) out.push({ start: d, end: new Date(d.getTime() + length), index: i });
  }
  return out;
}

export function createMockCalendar(me: () => Address): CalendarApi & { meetingOf: (messageId: string) => MeetingInfo | undefined } {
  let items: Stored[] | undefined;
  let owner = '';
  const all = () => {
    if (!items || owner !== me().address) {
      owner = me().address;
      items = seed(me());
    }
    return items;
  };
  let attendant: MyAttendantSettings = { enabled: true, placeholders: true, removeCancelled: false, attendantEnabled: true };
  // The mock user is a delegate of the board room and the pool car (approval needed).
  const monday = startOfWeek(new Date(), 1);
  const request = (id: string, resourceName: string, resource: string, resourceKind: 'room' | 'equipment', subject: string, organizer: Address, day: number, hour: number, hours: number, extra: Partial<BookingRequest> = {}): BookingRequest => ({
    id,
    resource,
    resourceName,
    resourceKind,
    subject,
    organizer: { name: organizer.name, address: organizer.address },
    start: at(addDays(monday, day), hour).toISOString(),
    end: at(addDays(monday, day), hour + hours).toISOString(),
    recurring: false,
    occurrences: 1,
    attendeeCount: 6,
    status: 'pending',
    createdAt: new Date(Date.now() - 3_600_000).toISOString(),
    ...extra,
  });
  const requests: BookingRequest[] = [
    request('br1', 'Board room', 'boardroom@example.com', 'room', 'Quarterly review', BOB, 3, 14, 2, { attendeeCount: 12 }),
    request('br2', 'Board room', 'boardroom@example.com', 'room', 'Customer workshop', CAROL, 8, 9, 3, { recurring: true, occurrences: 4 }),
    request('br3', 'Pool car 1', 'car-1@example.com', 'equipment', 'Site visit Springfield', CAROL, 2, 8, 9, { attendeeCount: 2 }),
    request('br4', 'Board room', 'boardroom@example.com', 'room', 'Budget planning', BOB, -2, 10, 1, { status: 'approved', decidedBy: 'alice@example.com', decidedAt: new Date(Date.now() - 86_400_000).toISOString() }),
  ];
  const foreign = { canCreate: false, canShare: false };
  let added: CalendarInfo[] = [
    { id: 'm.bob', kind: 'shared', owner: BOB, name: BOB.name, color: 'green', access: 'reviewer', ...foreign },
    { id: 'm.room1', kind: 'room', owner: ROOM_1, name: ROOM_1.name, color: 'orange', access: 'freebusy', ...foreign },
    { id: 'm.projector', kind: 'equipment', owner: PROJECTOR, name: PROJECTOR.name, color: 'purple', access: 'freebusy', ...foreign },
  ];
  /** Additional own calendars (folders below Calendar). */
  let extras: CalendarInfo[] = [{ id: HOLIDAYS_CALENDAR, kind: 'own', owner: me(), name: 'Holidays', color: 'teal', access: 'owner', canCreate: true, canShare: true }];
  let ownColor: CalendarInfo['color'] = 'blue';
  const sharing = new Map<string, CalendarPermissions['entries']>([
    [
      'own',
      [
        { principal: 'default', name: 'default', rights: ROLES.availabilityOnly, role: 'availabilityOnly' },
        { principal: 'anonymous', name: 'anonymous', rights: 0, role: 'none' },
        { principal: BOB.address, name: BOB.name, rights: ROLES.reviewer, role: 'reviewer' },
      ],
    ],
  ]);
  const permissionsOf = (calendarId: string): CalendarPermissions => ({
    calendarId,
    entries: sharing.get(calendarId) ?? [
      { principal: 'default', name: 'default', rights: ROLES.availabilityOnly, role: 'availabilityOnly' },
      { principal: 'anonymous', name: 'anonymous', rights: 0, role: 'none' },
    ],
    roles: Object.entries(ROLES).map(([role, rights]) => ({ role: role as PresetRole, rights })),
    rightBits: RIGHT_BITS,
    customRights: CUSTOM_RIGHTS,
  });

  const own = (): CalendarInfo => ({ id: 'own', kind: 'own', owner: me(), name: me().name, color: ownColor, access: 'owner', canCreate: true, canShare: true });
  const calendars = () => [own(), ...extras, ...added];
  const calendarOf = (id: string) => {
    const found = calendars().find((c) => c.id === id);
    if (!found) throw new ApiError(404, 'calendar_not_found', 'Calendar not found');
    return found;
  };
  /** Occurrence ids: `<item id>` or `<item id>.<index>`. */
  const parseId = (id: string) => {
    const [itemId = '', index] = id.split('.');
    const item = all().find((candidate) => candidate.id === itemId);
    if (!item) throw new ApiError(404, 'not_found', 'Event not found');
    return { item, index: index === undefined ? -1 : Number(index) };
  };
  /** Own extra calendars store their calendar id in `Stored.calendar`, everything else the owner's address. */
  const isExtra = (item: Stored) => extras.some((c) => c.id === item.calendar);
  const calendarIdOf = (item: Stored) =>
    isExtra(item) ? item.calendar : same(item.calendar, me().address) ? 'own' : (added.find((c) => same(c.owner.address, item.calendar))?.id ?? 'own');
  const accessOf = (item: Stored): Access =>
    isExtra(item) || same(item.calendar, me().address) ? 'owner' : (added.find((c) => same(c.owner.address, item.calendar))?.access ?? 'none');
  const inCalendar = (item: Stored, info: CalendarInfo) => (info.id.startsWith('f.') ? item.calendar === info.id : !isExtra(item) && same(item.calendar, info.owner.address));
  /** Like the API's rights gate: editors change everything, authors only what they created (the mock: what they organize). */
  const mayEdit = (item: Stored, access: Access) => access === 'owner' || access === 'editor' || (access === 'author' && item.myResponse === 'organizer');

  const toEvent = (item: Stored, occurrence: { start: Date; end: Date; index: number }): CalendarEvent => {
    const access = accessOf(item);
    const masked = (item.sensitivity === 'private' && access !== 'owner') || access === 'freebusy' || access === 'contributor' || access === 'none';
    const isOrganizer = item.myResponse === 'organizer';
    const readOnly = masked || !mayEdit(item, access);
    return {
      id: occurrence.index >= 0 ? `${item.id}.${occurrence.index}` : item.id,
      changeKey: 'ck',
      calendarId: calendarIdOf(item),
      subject: masked ? (item.sensitivity === 'private' ? 'Private appointment' : '') : item.subject,
      start: occurrence.start.toISOString(),
      end: occurrence.end.toISOString(),
      allDay: item.allDay,
      location: masked ? '' : item.location,
      showAs: item.showAs,
      sensitivity: item.sensitivity,
      isMeeting: item.attendees.length > 0,
      isOrganizer,
      organizer: masked ? null : item.organizer,
      attendees: masked ? [] : item.attendees,
      myResponse: item.myResponse,
      isRecurring: item.recurrence !== null,
      seriesId: item.recurrence ? item.id : null,
      isCancelled: false,
      masked,
      readOnly,
      canDelete: !readOnly,
      reminderMinutes: masked ? null : item.reminderMinutes,
      recurrence: masked ? null : item.recurrence,
      categories: masked ? [] : (item.categories ?? []),
      color: masked ? null : (item.color ?? null),
      onlineMeetingUrl: masked ? null : (item.onlineMeetingUrl ?? null),
      hasAttachments: false,
      reminders: masked ? [] : (item.reminders ?? []),
    };
  };

  const apply = (item: Stored, input: Partial<CalendarEventInput>) => {
    if (input.subject !== undefined) item.subject = input.subject;
    if (input.start !== undefined) item.start = new Date(input.start);
    if (input.end !== undefined) item.end = new Date(input.end);
    if (input.allDay !== undefined) item.allDay = input.allDay;
    if (input.location !== undefined) item.location = input.location;
    if (input.bodyHtml !== undefined) item.bodyHtml = input.bodyHtml;
    if (input.showAs !== undefined) item.showAs = input.showAs;
    if (input.categories !== undefined) item.categories = input.categories;
    if (input.color !== undefined) item.color = input.color;
    if (input.onlineMeetingUrl !== undefined) item.onlineMeetingUrl = input.onlineMeetingUrl;
    if (input.reminders !== undefined) item.reminders = input.reminders;
    if (input.sensitivity !== undefined) item.sensitivity = input.sensitivity;
    if (input.reminderMinutes !== undefined) item.reminderMinutes = input.reminderMinutes;
    if (input.recurrence !== undefined) item.recurrence = input.recurrence;
    if (input.attendees !== undefined) {
      item.attendees = input.attendees.map((a) => person({ name: a.name ?? DIRECTORY.find((d) => same(d.address, a.address))?.name ?? a.address, address: a.address }, a.type ?? 'required', 'none'));
      if (item.attendees.length > 0) item.organizer = me();
    }
  };

  const busyOf = (address: string, from: Date, to: Date) =>
    all()
      .filter((item) => same(item.calendar, address) || item.attendees.some((a) => same(a.address, address) && a.response !== 'declined'))
      .flatMap((item) => expand(item, from, to).map((o) => ({ start: o.start.toISOString(), end: o.end.toISOString(), showAs: item.showAs === 'free' ? ('busy' as const) : item.showAs })));

  return {
    listCalendars: () => delay(calendars()),
    addCalendar: async (address) => {
      const entry = DIRECTORY.find((d) => same(d.address, address));
      if (!entry) throw new ApiError(404, 'calendar_owner_not_found', 'No mailbox with this address');
      if (same(address, me().address)) throw new ApiError(400, 'own_calendar', 'Own calendar');
      const existing = added.find((c) => same(c.owner.address, address));
      if (existing) return delay(existing);
      const room = ROOMS.find((r) => same(r.address, address));
      const colors = ['teal', 'pink', 'yellow', 'red', 'green', 'orange', 'purple'] as const;
      const info: CalendarInfo = {
        id: `m.${address.split('@')[0] ?? address}`,
        kind: room ? room.kind : 'shared',
        owner: entry,
        name: entry.name,
        color: colors.find((c) => !added.some((a) => a.color === c)) ?? 'teal',
        access: ACCESS[address.toLowerCase()] ?? 'freebusy',
        canCreate: ['owner', 'editor', 'author', 'contributor'].includes(ACCESS[address.toLowerCase()] ?? ''),
        canShare: false,
      };
      added = [...added, info];
      return delay(info);
    },
    createOwnCalendar: async (name) => {
      if (calendars().some((c) => c.kind === 'own' && c.name.toLowerCase() === name.trim().toLowerCase())) throw new ApiError(409, 'folder_exists', 'A calendar with this name exists');
      const info: CalendarInfo = { id: `f.${Date.now().toString(36)}`, kind: 'own', owner: me(), name: name.trim(), color: 'teal', access: 'owner', canCreate: true, canShare: true };
      extras = [...extras, info];
      return delay(info);
    },
    updateCalendar: async (id, changes) => {
      const info = calendarOf(id);
      if (changes.name !== undefined && !id.startsWith('f.')) throw new ApiError(400, 'calendar_not_renamable', 'Only additional own calendars can be renamed');
      const next = { ...info, ...(changes.name !== undefined ? { name: changes.name } : {}), ...(changes.color !== undefined ? { color: changes.color } : {}) };
      if (id === 'own') ownColor = next.color;
      extras = extras.map((c) => (c.id === id ? next : c));
      added = added.map((c) => (c.id === id ? next : c));
      return delay(next);
    },
    removeCalendar: (id) => {
      added = added.filter((c) => c.id !== id);
      if (extras.some((c) => c.id === id)) {
        extras = extras.filter((c) => c.id !== id);
        items = all().filter((item) => item.calendar !== id);
        sharing.delete(id);
      }
      return delay(undefined);
    },
    listEvents: (ids, start, end) => {
      const from = new Date(start);
      const to = new Date(end);
      return delay(
        ids.map((calendarId) => {
          const info = calendars().find((c) => c.id === calendarId);
          if (!info) return { calendarId, events: [], error: { code: 'calendar_not_found', message: 'Calendar not found' } };
          if (info.access === 'none') return { calendarId, events: [], error: { code: 'calendar_access_denied', message: 'No access' } };
          const events = all()
            .filter((item) => inCalendar(item, info))
            .flatMap((item) => expand(item, from, to).map((o) => toEvent(item, o)));
          return { calendarId, events };
        }),
      );
    },
    getEvent: async (id) => {
      const { item, index } = parseId(id);
      if (!['owner', 'editor', 'author', 'reviewer'].includes(accessOf(item))) throw new ApiError(403, 'calendar_access_denied', 'No access');
      const occurrence = index >= 0 ? (expand(item, addDays(item.start, index), addDays(item.start, index + 1))[0] ?? { start: item.start, end: item.end, index }) : { start: item.start, end: item.end, index };
      const event = toEvent(item, occurrence);
      return delay({ ...event, bodyHtml: event.masked ? '' : item.bodyHtml });
    },
    createEvent: async (calendarId, input) => {
      const calendar = calendarOf(calendarId);
      if (!calendar.canCreate) throw new ApiError(403, 'calendar_access_denied', 'You cannot add events to this calendar');
      const item: Stored = {
        id: `ev-${Date.now()}`,
        calendar: calendar.id.startsWith('f.') ? calendar.id : calendar.owner.address,
        subject: '',
        start: new Date(input.start),
        end: new Date(input.end),
        allDay: false,
        location: '',
        bodyHtml: '',
        showAs: 'busy',
        sensitivity: 'normal',
        organizer: null,
        attendees: [],
        myResponse: 'organizer',
        reminderMinutes: 15,
        recurrence: null,
      };
      apply(item, input);
      all().push(item);
      return delay(toEvent(item, { start: item.start, end: item.end, index: -1 }));
    },
    updateEvent: async (id, changes, scope = 'occurrence') => {
      const { item, index } = parseId(id);
      if (!mayEdit(item, accessOf(item))) throw new ApiError(403, 'calendar_access_denied', 'Read only');
      if (item.myResponse !== 'organizer' && Object.keys(changes).some((k) => k !== 'showAs' && k !== 'reminderMinutes')) {
        throw new ApiError(403, 'meeting_attendee_readonly', 'Only the organizer can change this meeting');
      }
      if (item.recurrence && index >= 0) {
        const occurrence = { start: addDays(item.start, index), end: addDays(item.end, index) };
        if (scope === 'series') {
          // Like the server: the series moves by the offset given for the occurrence.
          const shift = (value: string | undefined, from: Date, base: Date) => (value === undefined ? undefined : new Date(base.getTime() + Date.parse(value) - from.getTime()).toISOString());
          apply(item, { ...changes, start: shift(changes.start, occurrence.start, item.start), end: shift(changes.end, occurrence.end, item.end) });
          return delay(toEvent(item, { start: item.start, end: item.end, index: -1 }));
        }
        // One occurrence: becomes its own item (an exception), the series skips it.
        item.skipped = [...(item.skipped ?? []), index];
        const exception: Stored = { ...structuredClone(item), id: `${item.id}-x${index}`, start: occurrence.start, end: occurrence.end, recurrence: null, skipped: undefined };
        apply(exception, changes);
        all().push(exception);
        return delay(toEvent(exception, { start: exception.start, end: exception.end, index: -1 }));
      }
      apply(item, changes);
      return delay(toEvent(item, { start: item.start, end: item.end, index: -1 }));
    },
    deleteEvent: (id, options = {}) => {
      const { item, index } = parseId(id);
      if (!mayEdit(item, accessOf(item))) throw new ApiError(403, 'calendar_access_denied', 'Read only');
      if (item.recurrence && index >= 0 && options.scope !== 'series') item.skipped = [...(item.skipped ?? []), index];
      else items = all().filter((candidate) => candidate !== item);
      return delay(undefined);
    },
    respondToEvent: (id, input) => {
      const { item } = parseId(id === INVITE_MESSAGE_ID ? INVITE_EVENT_ID : id);
      if (item.myResponse === 'organizer') throw new ApiError(400, 'not_an_invitation', 'Only invitations can be answered');
      item.myResponse = input.response === 'accept' ? 'accepted' : input.response === 'tentative' ? 'tentative' : 'declined';
      item.showAs = input.response === 'accept' ? 'busy' : 'tentative';
      item.attendees = item.attendees.map((a) => (same(a.address, me().address) ? { ...a, response: item.myResponse } : a));
      if (input.response === 'decline') items = all().filter((candidate) => candidate !== item);
      return delay(input.response === 'decline' ? null : item.id);
    },
    freeBusy: (attendees, start, end) => {
      const from = new Date(start);
      const to = new Date(end);
      return delay({
        attendees: attendees.map((address) => {
          const known = same(address, me().address) || DIRECTORY.some((d) => same(d.address, address));
          return known
            ? { address, error: null, busy: busyOf(address, from, to), workingHours: { daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'], startMinutes: 480, endMinutes: 1020, utcOffsetMinutes: -new Date().getTimezoneOffset() } }
            : { address, error: 'ErrorMailRecipientNotFound', busy: [], workingHours: null };
        }),
      });
    },
    schedulingSuggestions: input => {
      // Alias preview only; production suggestions are calculated from native availability by the API.
      const start = new Date(input.start); start.setHours(10, 0, 0, 0);
      return delay({ suggestions: [{ start: start.toISOString(), end: new Date(start.getTime() + input.durationMinutes * 60000).toISOString(), optionalBusy: 0 }] });
    },
    listRooms: () => delay(ROOMS),
    roomLists: () => delay({ lists: [] }),
    findRooms: input => delay({ checkedThrough: input.end, truncated: !!input.recurrence, rooms: ROOMS.filter(r => (r.capacity ?? 0) >= (input.capacity ?? 0) && (!input.building || r.building === input.building) && (!input.floor || r.floor === input.floor) && !input.features?.length).map(r => ({ ...r, features: [], listIds: [], occurrences: [{ start: input.start, end: input.end, state: 'free' as const }] })) }),
    calendarPermissions: async (calendarId) => {
      if (!calendarOf(calendarId).canShare) throw new ApiError(403, 'calendar_not_shareable', 'Only own calendars can be shared');
      return delay(permissionsOf(calendarId));
    },
    setCalendarPermissions: async (calendarId, input) => {
      if (!calendarOf(calendarId).canShare) throw new ApiError(403, 'calendar_not_shareable', 'Only own calendars can be shared');
      const current = permissionsOf(calendarId).entries;
      const entry = (principal: string, fallback: number) => {
        const rights = input.entries.find((e) => same(e.principal, principal))?.rights ?? fallback;
        const name = principal.includes('@') ? (DIRECTORY.find((d) => same(d.address, principal))?.name ?? principal) : principal;
        return { principal, name, rights, role: roleOf(rights) };
      };
      const special = (['default', 'anonymous'] as const).map((p) => entry(p, current.find((e) => e.principal === p)?.rights ?? 0));
      const people = input.entries
        .filter((e) => e.principal.includes('@') && e.rights !== 0)
        .map((e) => entry(e.principal, 0))
        .sort((a, b) => a.name.localeCompare(b.name));
      sharing.set(calendarId, [...special, ...people]);
      return delay(permissionsOf(calendarId));
    },
    myAttendantSettings: () => delay({ ...attendant }),
    setMyAttendantSettings: (input) => {
      attendant = { ...attendant, ...input };
      return delay({ ...attendant });
    },
    bookingRequests: () => delay(requests.map((r) => ({ ...r }))),
    decideBookingRequest: async (id, verdict, comment) => {
      const request = requests.find((r) => r.id === id);
      if (!request) throw new ApiError(404, 'booking_request_not_found', 'Booking request not found');
      if (request.status !== 'pending') throw new ApiError(409, 'booking_request_decided', 'This booking request was already decided');
      Object.assign(request, { status: verdict === 'approve' ? 'approved' : 'declined', decidedBy: me().address, decidedAt: new Date().toISOString(), ...(comment && { comment }) });
      return delay({ ...request });
    },
    /** Meeting block of the invitation mail (follows the answer). */
    meetingOf: (messageId) => {
      if (messageId !== INVITE_MESSAGE_ID) return undefined;
      const item = all().find((candidate) => candidate.id === INVITE_EVENT_ID);
      const start = item?.start ?? at(addDays(startOfWeek(new Date(), 1), 4), 11);
      return {
        kind: 'request',
        eventId: item ? INVITE_EVENT_ID : null,
        start: start.toISOString(),
        end: (item?.end ?? new Date(start.getTime() + 3_600_000)).toISOString(),
        location: 'Room 1',
        organizer: BOB,
        myResponse: item?.myResponse ?? 'declined',
        response: null,
      };
    },
  };
}
